import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { OUItem } from '@/api/types';
import { OuTreeRow, useOuExpansion } from '@/components/common/OuTree';

// ─── Shared types ─────────────────────────────────────────────────────────────

export type FieldDef = { label: string; key: string; value: number; min: number; max: number; suffix: string };

interface OUNumRowState { custom: Record<string, number> | null; editing: boolean; editBuf: Record<string, number>; }

interface OUSectionWrapperProps {
  isInModal?: boolean;
  onCloseModal?: () => void;
  modalTitle?: string;
  children: React.ReactNode;
}

// ─── OUSectionWrapper ─────────────────────────────────────────────────────────

function OUSectionWrapper({ isInModal, onCloseModal, modalTitle, children }: OUSectionWrapperProps) {
  const { t } = useTranslation();
  if (isInModal && onCloseModal) {
    return createPortal(
      <div className="modal-overlay">
        <div className="ou-modal">
          <div className="ou-modal-header">
            <span className="ou-modal-title">
              <i className="fas fa-sliders-h" aria-hidden="true" />
              {modalTitle}
              <span className="ou-modal-subtitle">— {t('admin.customizableByOU')}</span>
            </span>
            <button className="ou-modal-close" onClick={onCloseModal} aria-label={t('common.close')}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="ou-modal-body">
            <table className="rule-ou-table">
              <tbody>{children}</tbody>
            </table>
          </div>
        </div>
      </div>,
      document.body,
    );
  }
  return (
    <table className="rule-ou-table">
      <tbody>{children}</tbody>
    </table>
  );
}

// ─── RuleOUSection ────────────────────────────────────────────────────────────

export function RuleOUSection({ fields, items, isInModal, onCloseModal, modalTitle, onSaveOu, initialOuOverrides }: {
  fields: FieldDef[];
  items: OUItem[];
  isInModal?: boolean;
  onCloseModal?: () => void;
  modalTitle?: string;
  onSaveOu?: (ouId: string, values: Record<string, number> | null) => Promise<void>;
  initialOuOverrides?: Record<string, Record<string, number>>;
}) {
  const { t } = useTranslation();
  const { tree, isCollapsed, toggle } = useOuExpansion(items);
  const [rows, setRows] = useState<Record<string, OUNumRowState>>(() =>
    Object.fromEntries(items.map(item => {
      const saved = initialOuOverrides?.[item.id] ?? null;
      return [item.id, {
        custom: saved,
        editing: false,
        editBuf: saved ?? Object.fromEntries(fields.map(field => [field.key, field.value])),
      }];
    }))
  );

  const startEdit = (ouId: string) => setRows(previousRows => ({
    ...previousRows,
    [ouId]: { ...previousRows[ouId], editing: true, editBuf: previousRows[ouId].custom ?? Object.fromEntries(fields.map(field => [field.key, field.value])) },
  }));
  const saveEdit = async (ouId: string) => {
    const editBuffer = rows[ouId].editBuf;
    const isCustom = fields.some(field => editBuffer[field.key] !== field.value);
    const newValues = isCustom ? { ...editBuffer } : null;
    setRows(previousRows => ({ ...previousRows, [ouId]: { ...previousRows[ouId], custom: newValues, editing: false } }));
    if (onSaveOu) {
      try { await onSaveOu(ouId, newValues); }
      catch (saveError) {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.error('[RuleOUSection] save failed', saveError);
      }
    }
  };
  const cancelEdit  = (ouId: string) => setRows(previousRows => ({ ...previousRows, [ouId]: { ...previousRows[ouId], editing: false } }));
  const resetGlobal = async (ouId: string) => {
    setRows(previousRows => ({
      ...previousRows,
      [ouId]: { ...previousRows[ouId], custom: null, editing: false, editBuf: Object.fromEntries(fields.map(field => [field.key, field.value])) },
    }));
    if (onSaveOu) {
      try { await onSaveOu(ouId, null); }
      catch (resetError) {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.error('[RuleOUSection] reset failed', resetError);
      }
    }
  };

  const formatValues = (values: Record<string, number>) =>
    fields.map(field => `${values[field.key]} ${field.suffix}`).join(' · ');

  function renderCells(ouItem: OUItem) {
    const row = rows[ouItem.id];
    if (!row) return <td colSpan={2} />;

    if (row.editing) return (
      <>
        <td className="rule-ou-edit-fields">
          {fields.map(field => (
            <span key={field.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input type="number" className="rule-ou-num-input"
                value={row.editBuf[field.key]} min={field.min} max={field.max}
                onChange={event => setRows(previousRows => ({
                  ...previousRows, [ouItem.id]: { ...previousRows[ouItem.id], editBuf: { ...previousRows[ouItem.id].editBuf, [field.key]: Number(event.target.value) } },
                }))} />
              <span style={{ fontSize: '0.7rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>{field.suffix}</span>
            </span>
          ))}
        </td>
        <td className="rule-ou-edit-actions">
          <button className="rule-ou-save-btn" onClick={() => saveEdit(ouItem.id)}>
            <i className="fas fa-check" /> {t('admin.rulesSave')}
          </button>
          <button className="rule-ou-cancel-btn" onClick={() => cancelEdit(ouItem.id)} title={t('admin.rulesCancel')}>
            <i className="fas fa-times" />
          </button>
        </td>
      </>
    );

    if (row.custom) return (
      <>
        <td className="rule-ou-vals"><span className="rule-ou-val-custom">{formatValues(row.custom)}</span></td>
        <td className="rule-ou-status">
          <span className="rule-custom-badge">{t('admin.rulesCustom')}</span>
          <button className="rule-edit-ou-btn" onClick={() => startEdit(ouItem.id)} title={t('common.edit')}><i className="fas fa-pen" /></button>
          <button className="rule-reset-ou-btn" onClick={() => resetGlobal(ouItem.id)} title={t('common.resetToGlobal')}><i className="fas fa-undo" /></button>
        </td>
      </>
    );

    return (
      <>
        <td className="rule-ou-vals">
          <span className="rule-ou-val-inherit">
            {t('admin.rulesInheritsGlobal')} — {formatValues(Object.fromEntries(fields.map(field => [field.key, field.value])))}
          </span>
        </td>
        <td className="rule-ou-status">
          <span className="rule-inherit-badge">{t('admin.rulesInherited')}</span>
          <button className="rule-edit-ou-btn" onClick={() => startEdit(ouItem.id)} title={t('admin.rulesPersonalize')}><i className="fas fa-pen" /></button>
        </td>
      </>
    );
  }

  const content = tree.map(node => (
    <OuTreeRow key={node.ou.id} node={node} depth={0} isCollapsed={isCollapsed} onToggle={toggle} renderCells={renderCells} />
  ));

  return <OUSectionWrapper isInModal={isInModal} onCloseModal={onCloseModal} modalTitle={modalTitle}>{content}</OUSectionWrapper>;
}

// ─── RuleTimeOUSection ────────────────────────────────────────────────────────

interface OUTimeRowState { custom: { from: string; to: string } | null; editing: boolean; editBuf: { from: string; to: string }; }

export function RuleTimeOUSection({ globalFrom, globalTo, items, isInModal, onCloseModal, modalTitle }: {
  globalFrom: string;
  globalTo: string;
  items: OUItem[];
  isInModal?: boolean;
  onCloseModal?: () => void;
  modalTitle?: string;
}) {
  const { t } = useTranslation();
  const { tree, isCollapsed, toggle } = useOuExpansion(items);
  const [rows, setRows] = useState<Record<string, OUTimeRowState>>(() =>
    Object.fromEntries(items.map(item => [item.id, {
      custom: null, editing: false, editBuf: { from: globalFrom, to: globalTo },
    }]))
  );

  const startEdit  = (ouId: string) => setRows(previousRows => ({
    ...previousRows, [ouId]: { ...previousRows[ouId], editing: true, editBuf: previousRows[ouId].custom ?? { from: globalFrom, to: globalTo } },
  }));
  const saveEdit = (ouId: string) => setRows(previousRows => {
    const editBuffer = previousRows[ouId].editBuf;
    const isCustom = editBuffer.from !== globalFrom || editBuffer.to !== globalTo;
    return { ...previousRows, [ouId]: { ...previousRows[ouId], custom: isCustom ? { ...editBuffer } : null, editing: false } };
  });
  const cancelEdit = (ouId: string) => setRows(previousRows => ({ ...previousRows, [ouId]: { ...previousRows[ouId], editing: false } }));
  const resetGlobal = (ouId: string) => setRows(previousRows => ({
    ...previousRows, [ouId]: { ...previousRows[ouId], custom: null, editing: false, editBuf: { from: globalFrom, to: globalTo } },
  }));

  function renderCells(ouItem: OUItem) {
    const row = rows[ouItem.id];
    if (!row) return <td colSpan={2} />;

    if (row.editing) return (
      <>
        <td className="rule-ou-edit-fields">
          <input type="time" className="rule-ou-time-input" value={row.editBuf.from}
            onChange={event => setRows(previousRows => ({ ...previousRows, [ouItem.id]: { ...previousRows[ouItem.id], editBuf: { ...previousRows[ouItem.id].editBuf, from: event.target.value } } }))} />
          <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{t('admin.rn003TimeConnector')}</span>
          <input type="time" className="rule-ou-time-input" value={row.editBuf.to}
            onChange={event => setRows(previousRows => ({ ...previousRows, [ouItem.id]: { ...previousRows[ouItem.id], editBuf: { ...previousRows[ouItem.id].editBuf, to: event.target.value } } }))} />
        </td>
        <td className="rule-ou-edit-actions">
          <button className="rule-ou-save-btn" onClick={() => saveEdit(ouItem.id)}><i className="fas fa-check" /> {t('admin.rulesSave')}</button>
          <button className="rule-ou-cancel-btn" onClick={() => cancelEdit(ouItem.id)} title={t('admin.rulesCancel')}><i className="fas fa-times" /></button>
        </td>
      </>
    );

    if (row.custom) return (
      <>
        <td className="rule-ou-vals"><span className="rule-ou-val-custom">{row.custom.from} – {row.custom.to}</span></td>
        <td className="rule-ou-status">
          <span className="rule-custom-badge">{t('admin.rulesCustom')}</span>
          <button className="rule-edit-ou-btn" onClick={() => startEdit(ouItem.id)} title={t('common.edit')}><i className="fas fa-pen" /></button>
          <button className="rule-reset-ou-btn" onClick={() => resetGlobal(ouItem.id)} title={t('common.resetToGlobal')}><i className="fas fa-undo" /></button>
        </td>
      </>
    );

    return (
      <>
        <td className="rule-ou-vals">
          <span className="rule-ou-val-inherit">{t('admin.rulesInheritsGlobal')} — {globalFrom} {t('admin.rn003TimeConnector')} {globalTo}</span>
        </td>
        <td className="rule-ou-status">
          <span className="rule-inherit-badge">{t('admin.rulesInherited')}</span>
          <button className="rule-edit-ou-btn" onClick={() => startEdit(ouItem.id)} title={t('admin.rulesPersonalize')}><i className="fas fa-pen" /></button>
        </td>
      </>
    );
  }

  const content = tree.map(node => (
    <OuTreeRow key={node.ou.id} node={node} depth={0} isCollapsed={isCollapsed} onToggle={toggle} renderCells={renderCells} />
  ));

  return <OUSectionWrapper isInModal={isInModal} onCloseModal={onCloseModal} modalTitle={modalTitle}>{content}</OUSectionWrapper>;
}

// ─── Rn002OUSection ───────────────────────────────────────────────────────────

interface OUCheckRowState {
  recurr: boolean; work: boolean; keywords: string[];
  custom: boolean;
  editing: boolean;
  editRecurr: boolean; editWork: boolean; editKws: string[]; editKwInput: string;
}

export function Rn002OUSection({ labelRecur, labelWork, globalRecurr, globalWork, items, isInModal, onCloseModal, modalTitle, onSaveOu, initialOuOverrides }: {
  labelRecur: string; labelWork: string; globalRecurr: boolean; globalWork: boolean;
  items: OUItem[];
  isInModal?: boolean; onCloseModal?: () => void; modalTitle?: string;
  onSaveOu?: (ouId: string, state: { recurr: boolean; work: boolean; keywords: string[] } | null) => Promise<void>;
  initialOuOverrides?: Record<string, { recurr: boolean; work: boolean; keywords: string[] }>;
}) {
  const { t } = useTranslation();
  const { tree, isCollapsed, toggle } = useOuExpansion(items);
  const [rows, setRows] = useState<Record<string, OUCheckRowState>>(() =>
    Object.fromEntries(items.map(item => {
      const saved = initialOuOverrides?.[item.id] ?? null;
      return [item.id, {
        recurr: saved?.recurr ?? globalRecurr,
        work: saved?.work ?? globalWork,
        keywords: saved?.keywords ?? [],
        custom: !!saved,
        editing: false,
        editRecurr: saved?.recurr ?? globalRecurr,
        editWork: saved?.work ?? globalWork,
        editKws: saved?.keywords ?? [],
        editKwInput: '',
      }];
    }))
  );

  const startEdit = (ouId: string) => setRows(previousRows => ({
    ...previousRows, [ouId]: { ...previousRows[ouId], editing: true,
      editRecurr: previousRows[ouId].recurr, editWork: previousRows[ouId].work,
      editKws: [...previousRows[ouId].keywords], editKwInput: '' },
  }));

  const saveEdit = async (ouId: string) => {
    const currentRow = rows[ouId];
    const isCustom = currentRow.editRecurr !== globalRecurr
      || currentRow.editWork !== globalWork
      || currentRow.editKws.length > 0;
    const newState = isCustom
      ? { recurr: currentRow.editRecurr, work: currentRow.editWork, keywords: [...currentRow.editKws] }
      : null;
    setRows(previousRows => ({
      ...previousRows, [ouId]: { ...previousRows[ouId],
        recurr: currentRow.editRecurr, work: currentRow.editWork, keywords: [...currentRow.editKws],
        custom: isCustom, editing: false },
    }));
    if (onSaveOu) {
      try { await onSaveOu(ouId, newState); }
      catch (saveError) {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.error('[Rn002OUSection] save failed', saveError);
      }
    }
  };

  const cancelEdit = (ouId: string) => setRows(previousRows => ({ ...previousRows, [ouId]: { ...previousRows[ouId], editing: false } }));
  const resetGlobal = async (ouId: string) => {
    setRows(previousRows => ({
      ...previousRows, [ouId]: { ...previousRows[ouId],
        recurr: globalRecurr, work: globalWork, keywords: [], custom: false,
        editing: false, editRecurr: globalRecurr, editWork: globalWork, editKws: [], editKwInput: '' },
    }));
    if (onSaveOu) {
      try { await onSaveOu(ouId, null); }
      catch (resetError) {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.error('[Rn002OUSection] reset failed', resetError);
      }
    }
  };

  const addKeyword = (ouId: string) => setRows(previousRows => {
    const keyword = previousRows[ouId].editKwInput.trim();
    if (!keyword || previousRows[ouId].editKws.includes(keyword)) return previousRows;
    return { ...previousRows, [ouId]: { ...previousRows[ouId], editKws: [...previousRows[ouId].editKws, keyword], editKwInput: '' } };
  });
  const removeKeyword = (ouId: string, keyword: string) => setRows(previousRows => ({
    ...previousRows, [ouId]: { ...previousRows[ouId], editKws: previousRows[ouId].editKws.filter(existingKw => existingKw !== keyword) },
  }));

  const formatConfig = (row: OUCheckRowState) => {
    const parts = [row.recurr && labelRecur, row.work && labelWork].filter(Boolean) as string[];
    if (row.keywords.length) parts.push(`+${row.keywords.length} kw`);
    return parts.join(', ') || '—';
  };

  function renderCells(ouItem: OUItem) {
    const row = rows[ouItem.id];
    if (!row) return <td colSpan={2} />;

    if (row.editing) return (
      <>
        <td className="rule-ou-edit-fields" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
          <label className="checkbox-container" style={{ fontSize: '0.8rem' }}>
            <input type="checkbox" checked={row.editRecurr}
              onChange={event => setRows(previousRows => ({ ...previousRows, [ouItem.id]: { ...previousRows[ouItem.id], editRecurr: event.target.checked } }))} />
            <span className="checkmark" /><span>{labelRecur}</span>
          </label>
          {row.editRecurr && (
            <div className="rule-keyword-sub" style={{ marginTop: 0, width: '100%' }}>
              {row.editKws.length > 0 && (
                <div className="rule-kwchips">
                  {row.editKws.map(keyword => (
                    <span key={keyword} className="rule-kwchip">
                      {keyword}
                      <button className="btn btn--secondary" onClick={() => removeKeyword(ouItem.id, keyword)} title={t('admin.kwRemove')}>
                        <i className="fas fa-times" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="rule-kw-adder">
                <input type="text" className="rule-kw-input" placeholder={t('admin.rn002KwPlaceholder')}
                  value={row.editKwInput}
                  onChange={event => setRows(previousRows => ({ ...previousRows, [ouItem.id]: { ...previousRows[ouItem.id], editKwInput: event.target.value } }))}
                  onKeyDown={event => { if (event.key === 'Enter') addKeyword(ouItem.id); }} />
                <button className="rule-kw-add-btn" onClick={() => addKeyword(ouItem.id)} title={t('admin.kwAdd')}>
                  <i className="fas fa-plus" />
                </button>
              </div>
            </div>
          )}
          <label className="checkbox-container" style={{ fontSize: '0.8rem' }}>
            <input type="checkbox" checked={row.editWork}
              onChange={event => setRows(previousRows => ({ ...previousRows, [ouItem.id]: { ...previousRows[ouItem.id], editWork: event.target.checked } }))} />
            <span className="checkmark" /><span>{labelWork}</span>
          </label>
        </td>
        <td className="rule-ou-edit-actions">
          <button className="rule-ou-save-btn" onClick={() => saveEdit(ouItem.id)}><i className="fas fa-check" /> {t('admin.rulesSave')}</button>
          <button className="rule-ou-cancel-btn" onClick={() => cancelEdit(ouItem.id)} title={t('admin.rulesCancel')}><i className="fas fa-times" /></button>
        </td>
      </>
    );

    if (row.custom) return (
      <>
        <td className="rule-ou-vals"><span className="rule-ou-val-custom">{formatConfig(row)}</span></td>
        <td className="rule-ou-status">
          <span className="rule-custom-badge">{t('admin.rulesCustom')}</span>
          <button className="rule-edit-ou-btn" onClick={() => startEdit(ouItem.id)} title={t('common.edit')}><i className="fas fa-pen" /></button>
          <button className="rule-reset-ou-btn" onClick={() => resetGlobal(ouItem.id)} title={t('common.resetToGlobal')}><i className="fas fa-undo" /></button>
        </td>
      </>
    );

    return (
      <>
        <td className="rule-ou-vals"><span className="rule-ou-val-custom">{formatConfig(row)}</span></td>
        <td className="rule-ou-status">
          <span className="rule-inherit-badge">{t('admin.rulesGlobal')}</span>
          <button className="rule-edit-ou-btn" onClick={() => startEdit(ouItem.id)} title={t('admin.rulesPersonalize')}><i className="fas fa-pen" /></button>
        </td>
      </>
    );
  }

  const content = tree.map(node => (
    <OuTreeRow key={node.ou.id} node={node} depth={0} isCollapsed={isCollapsed} onToggle={toggle} renderCells={renderCells} />
  ));

  return <OUSectionWrapper isInModal={isInModal} onCloseModal={onCloseModal} modalTitle={modalTitle}>{content}</OUSectionWrapper>;
}
